import { useEffect } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { MaskedLine, EASE, scrollToId, CtaPrimary, CtaGhost } from "./Shared";

const HERO_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_frame-gallery-162/artifacts/cfsmhh6z_ferrari%20shoot-14.webp";

export default function Hero({ loaded }) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const sx = useSpring(mx, { stiffness: 50, damping: 18 });
  const sy = useSpring(my, { stiffness: 50, damping: 18 });
  const bgX = useTransform(sx, [-0.5, 0.5], ["-2.5%", "2.5%"]);
  const bgY = useTransform(sy, [-0.5, 0.5], ["-1.5%", "1.5%"]);
  const rotX = useTransform(sy, [-0.5, 0.5], [2.5, -2.5]);
  const rotY = useTransform(sx, [-0.5, 0.5], [-3, 3]);

  useEffect(() => {
    const onMove = (e) => {
      mx.set(e.clientX / window.innerWidth - 0.5);
      my.set(e.clientY / window.innerHeight - 0.5);
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, [mx, my]);

  const anim = (delay) => ({
    initial: { y: "112%" },
    animate: loaded ? { y: 0 } : { y: "112%" },
    transition: { duration: 1.1, ease: EASE, delay },
  });

  return (
    <section
      className="relative min-h-screen flex flex-col justify-end overflow-hidden"
      style={{ perspective: "1200px" }}
      data-testid="hero-section"
    >
      <motion.div
        className="absolute inset-0 md:left-auto md:w-[62%]"
        style={{ x: bgX, y: bgY, rotateX: rotX, rotateY: rotY, scale: 1.02 }}
      >
        <img
          src={HERO_IMG}
          alt="Ferrari garage shoot"
          className="hero-blend-img w-full h-full object-cover object-center md:object-[center_55%] opacity-90"
        />
        <div className="absolute inset-0 hero-vignette" />
      </motion.div>

      <div className="absolute top-24 right-5 md:right-10 flex items-center gap-3 z-10">
        <span className="w-2.5 h-2.5 rounded-full bg-[#FF3B30] animate-blink" />
        <span className="font-mono text-[11px] tracking-[0.3em] text-zinc-300">REC</span>
        <span className="font-mono text-[11px] tracking-[0.2em] text-zinc-600 hidden sm:inline">— 23.976 FPS / 4K</span>
      </div>

      <div className="relative z-10 max-w-[1440px] w-full mx-auto px-5 md:px-10 pb-16 md:pb-24 pt-40">
        <span className="block overflow-hidden">
          <motion.p {...anim(0.55)} className="font-mono text-[11px] md:text-xs uppercase tracking-[0.35em] text-gold mb-5">
            Diego Tomazo — Greater Toronto Area
          </motion.p>
        </span>

        <h1 className="font-display uppercase leading-[0.88] tracking-tighter">
          <MaskedLine animate={loaded} delay={0.7}>
            <span className="block text-[10.5vw] sm:text-[12vw] lg:text-[10.5rem] font-extrabold text-zinc-100">
              Tomazo
            </span>
          </MaskedLine>
          <MaskedLine animate={loaded} delay={0.85}>
            <span className="block font-bebas font-normal text-[14vw] sm:text-[14vw] lg:text-[11.5rem] tracking-[0.06em] text-outline">
              Productions
            </span>
          </MaskedLine>
        </h1>

        <span className="block overflow-hidden mt-6 max-w-xl">
          <motion.p {...anim(1.05)} className="font-serifaccent italic text-lg md:text-2xl text-zinc-300 leading-snug">
            “Cinematic videos and sharp, high-value photography — delivered at an accessible cost.”
          </motion.p>
        </span>

        <span className="block overflow-hidden mt-10">
          <motion.div {...anim(1.2)} className="flex flex-wrap gap-4">
            <CtaPrimary testid="hero-cta-explore" onClick={() => scrollToId("#work")}>
              Explore the Reel
            </CtaPrimary>
            <CtaGhost testid="hero-cta-book" onClick={() => scrollToId("#booking")}>
              Book a Shoot
              <ArrowRight size={14} className="transition-transform duration-300 group-hover:translate-x-1" />
            </CtaGhost>
          </motion.div>
        </span>
      </div>

      <motion.div
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 text-zinc-500"
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
      >
        <ChevronDown size={20} />
      </motion.div>
    </section>
  );
}
